import { Box, Stack, Typography, Skeleton } from "@mui/material";
import { ResponsiveContainer, Area, XAxis, YAxis, CartesianGrid, Tooltip, Line, ComposedChart } from "recharts";
import { useAppTheme } from "@/hooks/useAppTheme";
import { MonthlyStat } from "../hooks/useLandingPageData";

interface ClaimsActivityChartProps {
  data: MonthlyStat[];
  loading?: boolean;
  /** When set, prior year comparison line is shown and labeled accordingly */
  selectedYear?: number;
}

const REIMBURSED_COLOR = "#059669";
const PRIOR_YEAR_COLOR = "#9CA3AF"; // gray-400

export const ClaimsActivityChart = ({ data, loading = false, selectedYear }: ClaimsActivityChartProps) => {
  const { theme, isDarkMode } = useAppTheme();
  const primaryColor = theme.palette.primary.main;
  const reimbursedColor = isDarkMode ? "#34D399" : REIMBURSED_COLOR;
  const priorYearColor = isDarkMode ? "#6B7280" : PRIOR_YEAR_COLOR;

  if (loading) {
    return <Skeleton variant="rounded" height={200} />;
  }

  const hasData = data.some((d) => d.claims > 0);
  const hasPriorYear = data.some((d) => (d.priorYearClaims ?? 0) > 0);

  if (!hasData && !hasPriorYear) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 200 }}>
        <Typography variant="body2" color="text.secondary">
          No claims activity data
        </Typography>
      </Box>
    );
  }

  const currentLabel = selectedYear ? `${selectedYear} Claims` : "Total Claims";
  const priorLabel = selectedYear ? `${selectedYear - 1} Claims` : "";

  return (
    <>
      <ResponsiveContainer width="100%" height={220}>
        <ComposedChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
          <defs>
            <linearGradient id="claimsGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={primaryColor} stopOpacity={0.2} />
              <stop offset="95%" stopColor={primaryColor} stopOpacity={0} />
            </linearGradient>
            <linearGradient id="reimbursedGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={reimbursedColor} stopOpacity={0.15} />
              <stop offset="95%" stopColor={reimbursedColor} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme.palette.divider} />
          <XAxis dataKey="name" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
          <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
          <Tooltip
            contentStyle={{
              borderRadius: 8,
              border: `1px solid ${theme.palette.divider}`,
              fontSize: 12,
              backgroundColor: theme.palette.background.paper,
            }}
            formatter={(value: number, name: string) => {
              const labels: Record<string, string> = {
                claims: currentLabel,
                reimbursed: "Reimbursed",
                priorYearClaims: priorLabel,
              };
              return [value, labels[name] || name];
            }}
          />

          {/* Prior year — dashed line behind current data */}
          {hasPriorYear && (
            <Line
              type="monotone"
              dataKey="priorYearClaims"
              stroke={priorYearColor}
              strokeWidth={1.5}
              strokeDasharray="6 3"
              dot={false}
              activeDot={{ r: 3, strokeWidth: 0, fill: priorYearColor }}
            />
          )}

          {/* Current year areas */}
          <Area
            type="monotone"
            dataKey="claims"
            stroke={primaryColor}
            strokeWidth={2}
            fill="url(#claimsGradient)"
            dot={false}
            activeDot={{ r: 4, strokeWidth: 0, fill: primaryColor }}
          />
          <Area
            type="monotone"
            dataKey="reimbursed"
            stroke={reimbursedColor}
            strokeWidth={2}
            fill="url(#reimbursedGradient)"
            dot={false}
            activeDot={{ r: 4, strokeWidth: 0, fill: reimbursedColor }}
          />
        </ComposedChart>
      </ResponsiveContainer>

      {/* Legend */}
      <Stack direction="row" spacing={2.5} justifyContent="center" sx={{ mt: 0.5 }}>
        <LegendItem color={primaryColor} label={currentLabel} />
        <LegendItem color={reimbursedColor} label="Reimbursed" />
        {hasPriorYear && <LegendItem color={priorYearColor} label={priorLabel} dashed />}
      </Stack>
    </>
  );
};

/** Inline legend swatch — solid or dashed line + label */
const LegendItem = ({ color, label, dashed = false }: { color: string; label: string; dashed?: boolean }) => (
  <Stack direction="row" alignItems="center" spacing={0.75}>
    <Box
      sx={{
        width: 16,
        height: 0,
        borderTop: `2px ${dashed ? "dashed" : "solid"} ${color}`,
      }}
    />
    <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
      {label}
    </Typography>
  </Stack>
);
